import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table as ChakraTable,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import {
  useEffect,
  useState
} from "react";

import { useMissionsApi } from "src/api/missions/missions.api";
import { Mission } from "src/api/missions/missions";
import {
  DestructiveButton,
  SolidButton
} from "src/components/button";
import { Header } from "src/components/shared/Header";
import { Text } from "src/components/text";
import { formatUtcDate } from "src/utils/utcDate";

export const MissionsAdminPage: React.FC = () => {
  const history = useHistory();
  const toast = useToast();
  const [ missionsRes, missionsReq ] = useMissionsApi("GET_MISSIONS");
  const [ deleteRes, deleteReq ] = useMissionsApi("DELETE_MISSION");
  const [ missionToDelete, setMissionToDelete ] = useState<Mission | null>(null);

  useEffect(() => {
    missionsReq({});
  }, [ missionsReq ]);

  useEffect(() => {
    if (deleteRes.data) {
      toast({
        status: "success",
        title: "Mission deleted"
      });
      missionsReq({});
    }

    if (deleteRes.error) {
      toast({
        status: "error",
        title: "Could not delete mission",
        description: deleteRes.error.details
      });
    }
  }, [
    deleteRes,
    missionsReq,
    toast
  ]);

  const missions = missionsRes.data?.results || [];

  return (
    <Box p={10}>
      <Modal
        isOpen={!!missionToDelete}
        onClose={() => setMissionToDelete(null)}
      >
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>
            Delete mission
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Text>
              {`Delete ${missionToDelete?.name}? People in this mission will move to GENERAL.`}
            </Text>
          </ModalBody>

          <ModalFooter>
            <SolidButton
              mr={3}
              onClick={() => setMissionToDelete(null)}
            >
              Close
            </SolidButton>

            <DestructiveButton
              onClick={() => {
                if (!missionToDelete) {
                  return;
                }

                deleteReq({ id: missionToDelete._id });
                setMissionToDelete(null);
              }}
            >
              Delete mission
            </DestructiveButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Header />

      <Box paddingY={10}>
        <Text as="h1">
          All Missions
        </Text>

        <Box
          paddingY={4}
          display="flex"
          justifyContent="space-between"
        >
          <Text as="p">
            View and manage missions. This is an admin-only page.
          </Text>

          <SolidButton
            onClick={() => history.push("/missions/create")}
          >
            Create mission
          </SolidButton>
        </Box>

        <ChakraTable variant="striped">
          <Thead>
            <Tr>
              <Th>
                Name
              </Th>

              <Th>
                Start
              </Th>

              <Th>
                End
              </Th>

              <Th>
                Actions
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {missions.map(mission => (
              <Tr key={mission._id}>
                <Td>
                  {mission.name}
                </Td>

                <Td>
                  {formatUtcDate(mission.startDate)}
                </Td>

                <Td>
                  {mission.endDate ? formatUtcDate(mission.endDate) : "No end date"}
                </Td>

                <Td>
                  <Box display="flex">
                    <SolidButton
                      onClick={() => history.push(`/missions/${mission._id}/edit`)}
                    >
                      Edit
                    </SolidButton>

                    {!mission.isGeneral && (
                      <DestructiveButton
                        ml={3}
                        onClick={() => setMissionToDelete(mission)}
                      >
                        Delete
                      </DestructiveButton>
                    )}
                  </Box>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </ChakraTable>
      </Box>
    </Box>
  );
};
